"use client";

import * as React from "react";
import { Tooltip as TooltipPrimitive } from "radix-ui";

import { cn } from "@/design-system/cn.ts";

// Shared timing for every hint in the app. 320ms is long enough that sweeping
// the cursor across a row of controls doesn't strobe, short enough to feel
// responsive; skipDelayDuration lets a neighbouring hint open instantly once
// one is already showing.
const ENTER_DELAY_MS = 320;
const SKIP_DELAY_MS = 400;

function TooltipProvider({
  delayDuration = ENTER_DELAY_MS,
  skipDelayDuration = SKIP_DELAY_MS,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      {...props}
    />
  );
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

// Keep hints inside the viewport. Radix flips sides on collision by default,
// but without padding it will still park a tooltip flush against an edge — and
// several of these hang off controls pinned to the very edge of the screen.
const COLLISION_PADDING = 12;

function TooltipContent({
  className,
  sideOffset = 0,
  collisionPadding = COLLISION_PADDING,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        avoidCollisions
        className={cn(
          "z-50 w-fit max-w-[calc(100vw-1.5rem)] origin-(--radix-tooltip-content-transform-origin) animate-in rounded-lg border border-rule bg-ink-800 px-2.5 py-1.5 text-xs text-balance text-chalk shadow-[0_12px_32px_-12px_var(--console-shadow)] fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="z-50 size-2 translate-y-[calc(-50%_-_1px)] rotate-45 rounded-[2px] border-r border-b border-rule bg-ink-800 fill-ink-800" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
